import { Unit } from './units'

const UNITS_CSV_URL =
  'https://docs.google.com/spreadsheets/d/e/2PACX-1vSXZUU7nGHYrlO_cZ-Ghdejo78Gl5hXzvUD35Qz1ydD2JRCEIHQQVYukulK3p4rtBKwbqLtKVnNcj8p/pub?output=csv'

function parseNumber(value: string): number | null {
  const numericValue = value
    .replace(/\u00a0/g, ' ')
    .replace(/[^\d,.-]/g, '')
    .trim()

  if (!numericValue || numericValue === '—') return null

  const lastComma = numericValue.lastIndexOf(',')
  const lastDot = numericValue.lastIndexOf('.')
  const decimalSeparator = lastComma > lastDot ? ',' : '.'
  const decimalIndex = Math.max(lastComma, lastDot)

  const normalized =
    decimalIndex >= 0 && numericValue.length - decimalIndex <= 3
      ? `${numericValue.slice(0, decimalIndex).replace(/[,.]/g, '')}.${numericValue.slice(decimalIndex + 1)}`
      : numericValue.replace(/[,.]/g, '')

  const number = Number.parseFloat(normalized)
  return Number.isNaN(number) ? null : number
}

function parseStav(value: string): Unit['stav'] {
  const lower = value.trim().toLowerCase()
  if (lower.includes('prodán') || lower.includes('prodano')) return 'prodáno'
  if (lower.includes('rezerv')) return 'rezervováno'
  if (lower.includes('blok') || lower.includes('blocked')) return 'blokováno'
  return 'k dispozici'
}

function normalizeHeader(value: string): string {
  return value
    .toLocaleLowerCase('cs-CZ')
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '')
    .replace(/[^\p{L}\p{N}]+/gu, ' ')
    .trim()
}

function findColumnIndex(headers: string[], ...keywords: string[]): number {
  return headers.findIndex((header) => {
    const normalizedHeader = normalizeHeader(header)
    return keywords.some((keyword) =>
      normalizedHeader.includes(normalizeHeader(keyword)),
    )
  })
}

function detectDelimiter(headerRow: string): string {
  const delimiters = [';', ',', '\t']
  return delimiters.reduce((bestDelimiter, delimiter) => {
    const bestCount = headerRow.split(bestDelimiter).length
    const count = headerRow.split(delimiter).length
    return count > bestCount ? delimiter : bestDelimiter
  }, ';')
}

function parseCsv(text: string, delimiter: string): string[][] {
  const rows: string[][] = []
  let row: string[] = []
  let cell = ''
  let inQuotes = false

  for (let index = 0; index < text.length; index += 1) {
    const character = text[index]
    const nextCharacter = text[index + 1]

    if (character === '"' && inQuotes && nextCharacter === '"') {
      cell += '"'
      index += 1
    } else if (character === '"') {
      inQuotes = !inQuotes
    } else if (character === delimiter && !inQuotes) {
      row.push(cell.trim())
      cell = ''
    } else if ((character === '\n' || character === '\r') && !inQuotes) {
      if (character === '\r' && nextCharacter === '\n') index += 1
      row.push(cell.trim())
      if (row.some((value) => value)) rows.push(row)
      row = []
      cell = ''
    } else {
      cell += character
    }
  }

  row.push(cell.trim())
  if (row.some((value) => value)) rows.push(row)
  return rows
}

export async function fetchUnitsFromSheet(): Promise<Unit[]> {
  const response = await fetch(UNITS_CSV_URL, { cache: 'no-store' })
  if (!response.ok) throw new Error(`Failed to fetch unit CSV: ${response.status}`)

  const text = (await response.text()).replace(/^\uFEFF/, '')
  const delimiter = detectDelimiter(text.split(/\r?\n/, 1)[0])
  const rows = parseCsv(text, delimiter)
  if (rows.length < 2) throw new Error('Unit CSV has no data rows')

  const headers = rows[0]

  const colVchod = findColumnIndex(headers, 'vchod')
  const colPatro = findColumnIndex(headers, 'patro')
  const colKod = findColumnIndex(
    headers,
    'kód jednotky',
    'kod jednotky',
    'označení jednotky',
    'oznaceni jednotky',
    'jednotka',
  )
  const colTyp = findColumnIndex(headers, 'typ jednotky', 'typ')
  const colKategorie = findColumnIndex(headers, 'kategorie', 'dispozice')
  const colPlocha = findColumnIndex(
    headers,
    'podlahová plocha',
    'podlahova plocha',
    'plocha m2 bytu',
    'plocha bytu',
    'užitná plocha',
    'uzitna plocha',
  )
  const colBalkon = findColumnIndex(headers, 'balkon')
  const colTerasa = findColumnIndex(headers, 'terasa')
  const colPredzahradka = findColumnIndex(
    headers,
    'předzahrádka',
    'predzahradka',
    'předzahr',
  )
  const colStav = findColumnIndex(headers, 'stav', 'status')
  const colCenaSDph = findColumnIndex(
    headers,
    'cena s dph',
    'cena vcetne dph',
    'cena vc dph',
    'cena jednotky s dph',
    'cena jednotky vcetne dph',
    'prodejni cena s dph',
    'celkova cena s dph',
  )

  const units: Unit[] = []

  for (let index = 1; index < rows.length; index += 1) {
    const columns = rows[index]
    const kod = colKod >= 0 ? columns[colKod] : ''
    const uzitnaPlocha = colPlocha >= 0 ? parseNumber(columns[colPlocha]) : null

    if (!kod || uzitnaPlocha === null) continue

    units.push({
      id: String(index),
      vchod: colVchod >= 0 ? columns[colVchod] || '' : '',
      patro: colPatro >= 0 ? columns[colPatro] || '' : '',
      kod,
      typ: colTyp >= 0 ? columns[colTyp] || 'bytová jednotka' : 'bytová jednotka',
      kategorie: colKategorie >= 0 ? columns[colKategorie] || '' : '',
      uzitnaPlochM2: uzitnaPlocha,
      balkonM2: colBalkon >= 0 ? parseNumber(columns[colBalkon]) : null,
      terasaM2: colTerasa >= 0 ? parseNumber(columns[colTerasa]) : null,
      predzahradkaM2:
        colPredzahradka >= 0 ? parseNumber(columns[colPredzahradka]) : null,
      cenaSDph: colCenaSDph >= 0 ? parseNumber(columns[colCenaSDph]) : null,
      stav: colStav >= 0 ? parseStav(columns[colStav]) : 'k dispozici',
    })
  }

  if (units.length === 0) throw new Error('Unit CSV contains no valid units')

  return units
}
